import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sparkles, ShoppingBag, User } from "lucide-react";
import { useSiteContent } from "../contexts/SiteContent";
import { useCustomer, useCart } from "../shop";

const ANCHORS = [
  { key: "nav.science", fallback: "Science", scrollTo: "#top" },
  { key: "nav.models", fallback: "Models", scrollTo: "#top" },
  { key: "nav.research", fallback: "Research", scrollTo: "#breakthroughs" },
];

export default function Nav() {
  const { t } = useSiteContent();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { customer, logout } = useCustomer();
  const { count } = useCart();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setUserOpen(false); }, [location.pathname]);

  const handleAnchorClick = (e, anchor) => {
    if (!onLanding) return;
    e.preventDefault();
    const id = anchor.replace(/^#/, "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="top-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-neutral-200/70" : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" state={{ scrollTo: "#top" }} data-testid="brand-wordmark"
          className="text-[17px] font-semibold tracking-tight text-neutral-900">
          {t("nav.brand", "ScienceAI")}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {ANCHORS.map((link, i) => (
            <Link key={link.key} to="/" state={{ scrollTo: link.scrollTo }}
              onClick={(e) => handleAnchorClick(e, link.scrollTo)}
              data-testid={`nav-link-${link.fallback.toLowerCase()}`}
              className={`text-[14px] font-medium transition-colors ${
                i === 0 ? "text-neutral-900 underline underline-offset-[10px] decoration-2" : "text-neutral-600 hover:text-neutral-900"
              }`}>
              {t(link.key, link.fallback)}
            </Link>
          ))}
          <Link to={t("nav.shop_href", "/shop")} data-testid="nav-link-shop"
            className={`text-[14px] font-medium transition-colors ${
              location.pathname.startsWith("/shop") ? "text-neutral-900 underline underline-offset-[10px] decoration-2" : "text-neutral-600 hover:text-neutral-900"
            }`}>
            {t("nav.shop", "Shop")}
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/cart" data-testid="nav-cart-icon"
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-neutral-100">
            <ShoppingBag className="h-4 w-4 text-neutral-900" />
            {count > 0 && (
              <span data-testid="nav-cart-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-neutral-900 text-white text-[10px] font-semibold flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>

          <div className="relative">
            {customer ? (
              <button onClick={() => setUserOpen((v) => !v)} data-testid="nav-customer-popover-toggle"
                className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 h-8 text-[12px] font-medium text-neutral-800 hover:bg-neutral-200">
                <User className="h-3.5 w-3.5" />
                {customer.email.split("@")[0]}
              </button>
            ) : (
              <Link to="/account/login" data-testid="nav-signin-pill"
                className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-3 h-8 text-[12px] font-medium text-white hover:bg-neutral-800">
                Sign in
              </Link>
            )}
            {customer && userOpen && (
              <div data-testid="nav-customer-menu"
                className="absolute right-0 mt-2 w-56 rounded-xl border border-neutral-200 bg-white shadow-lg p-2">
                <div className="px-3 py-2 text-[11px] text-neutral-500 truncate border-b border-neutral-100">{customer.email}</div>
                <Link to="/account" className="block px-3 py-2 text-[13px] hover:bg-neutral-100 rounded-md" data-testid="nav-menu-account">Account</Link>
                <Link to="/orders" className="block px-3 py-2 text-[13px] hover:bg-neutral-100 rounded-md" data-testid="nav-menu-orders">Orders</Link>
                <button onClick={() => { logout(); setUserOpen(false); }} data-testid="nav-menu-signout"
                  className="block w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-100 rounded-md text-rose-600">
                  Sign out
                </button>
              </div>
            )}
          </div>

          <span data-testid="built-with-gemini"
            className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-[12px] font-medium text-neutral-700">
            <Sparkles className="h-3.5 w-3.5 text-blue-600" strokeWidth={2.2} />
            {t("nav.badge", "Built with Gemini")}
          </span>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-neutral-900" aria-label="Toggle menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-neutral-200 bg-white">
          <div className="px-4 py-4 flex flex-col gap-3">
            {ANCHORS.map((link) => (
              <Link key={link.key} to="/" state={{ scrollTo: link.scrollTo }}
                onClick={(e) => { setOpen(false); handleAnchorClick(e, link.scrollTo); }}
                data-testid={`mobile-nav-link-${link.fallback.toLowerCase()}`}
                className="text-[15px] py-1 text-neutral-700">
                {t(link.key, link.fallback)}
              </Link>
            ))}
            <Link to={t("nav.shop_href", "/shop")} onClick={() => setOpen(false)} data-testid="mobile-nav-link-shop"
              className="text-[15px] py-1 text-neutral-700">{t("nav.shop", "Shop")}</Link>
            <Link to="/cart" onClick={() => setOpen(false)} data-testid="mobile-nav-link-cart"
              className="text-[15px] py-1 text-neutral-700">Cart ({count})</Link>
            {!customer ? (
              <Link to="/account/login" onClick={() => setOpen(false)} data-testid="mobile-nav-link-signin"
                className="text-[15px] py-1 text-neutral-900 font-semibold">Sign in</Link>
            ) : (
              <>
                <Link to="/account" onClick={() => setOpen(false)} className="text-[15px] py-1 text-neutral-700">Account</Link>
                <Link to="/orders" onClick={() => setOpen(false)} className="text-[15px] py-1 text-neutral-700">Orders</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="text-left text-[15px] py-1 text-rose-600">Sign out</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
